import BotButtons from '@components/Common/BotButtons/index';
import { Box, makeStyles, Paper, useTheme } from '@material-ui/core';
import ConversationalAgent from '@src/components/ConversationalAgent';
import Typography from '@src/components/UI/Typography';
import { appConfig } from '@src/config/appConfig';
import useActiveAdminOption from '@src/hooks/useActiveAdminOption';
import { getUserState } from '@src/reducers/userState';
import { selectAuthenticationInfo, userState } from '@src/selectors/user';
import type { RootState } from '@src/store/reduxstore';
import { LoginSuccess, Logout } from '@src/types';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import {
  AdminAction,
  AgentChatAction,
  LogoutAction,
  NotificationsAnnounceKitAction,
} from './actions';
import HeaderTightMenu from './HeaderTightMenu';
import { ArrowDownIcon } from './SvgIcons';
import UserAvatar from './UserAvatar';
import { AvatarWithStatusMenu } from './Utils';

export interface IHeaderAdmin {
  consoleHeader?: boolean;
  tight?: boolean;
}

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    padding: '12px 10px',
    color: theme.palette.text.primary,
    width: '100%',
  },
}));

const HeaderMenu: React.FC<IHeaderAdmin> = ({
  consoleHeader = false,
  tight = false,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const location = useLocation();
  const { root } = useStyles();

  const state = useSelector(userState);
  const user: LoginSuccess = useSelector(selectAuthenticationInfo);
  const adminOptionActive = useActiveAdminOption();
  const logOutState = useSelector(({ logOut }: RootState) => logOut as Logout);

  const adminOptionChildActive = adminOptionActive?.childrens?.find(
    childOption => childOption.url === location.pathname
  );

  const adminOptionChildActiveSubPathUrl = adminOptionActive?.subPaths?.find(
    subPath => location.pathname.indexOf(subPath.url) !== -1
  );
  const hasChildrenOrSubPaths =
    adminOptionChildActive || adminOptionChildActiveSubPathUrl;

  useEffect(() => {
    dispatch(getUserState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user) window.location.replace('/login');
  }, [user]);

  useEffect(() => {
    if (logOutState.status) {
      appConfig.deleteStoreData();
      window.location.replace('/login');
    }
  }, [logOutState]);

  return (
    <Paper
      elevation={2}
      style={{
        borderRadius: '8px 0 8px 8px',
        position: 'relative',
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        {!consoleHeader && (
          <Box
            display="flex"
            whiteSpace="nowrap"
            alignItems="center"
            pl={2}
            style={{ gap: hasChildrenOrSubPaths ? '12px' : '32px' }}
          >
            <Typography
              variant="h2"
              style={{
                width: 'inherit',
                fontSize: 26,
                color: theme.palette.text.primary,
                fontWeight: 500,
                ...(hasChildrenOrSubPaths
                  ? {
                      borderRight: `2px solid ${theme.palette.text.primary}`,
                      paddingRight: '12px',
                      lineHeight: '25px',
                    }
                  : {}),
              }}
            >
              {adminOptionActive && (
                <FormattedMessage id={adminOptionActive?.title} />
              )}
            </Typography>

            {hasChildrenOrSubPaths && (
              <Typography
                variant="h2"
                color="primary"
                style={{
                  fontSize: 26,
                  fontWeight: 600,
                  fontStyle: 'SemiBold',
                  lineHeight: '124%',
                }}
              >
                <FormattedMessage
                  id={
                    adminOptionChildActive?.title ||
                    adminOptionChildActiveSubPathUrl?.title
                  }
                />
              </Typography>
            )}
            <BotButtons />
          </Box>
        )}
        <Box
          className={root}
          justifyContent={!consoleHeader ? 'flex-end' : 'space-between'}
        >
          <Box position="relative" style={{ display: tight ? 'flex' : 'none' }}>
            <UserAvatar imageUrl={user.avatarUrl} isOnline={state} />
            <HeaderTightMenu>
              <Box
                position="absolute"
                right={-10}
                bottom={-17}
                style={{ cursor: 'pointer' }}
              >
                {ArrowDownIcon}
              </Box>
            </HeaderTightMenu>
          </Box>

          <Box
            display={!tight ? 'flex' : 'none'}
            width="100%"
            style={
              !consoleHeader
                ? {
                    justifyContent: 'flex-end',
                    gap: '16px',
                  }
                : { justifyContent: 'space-between', gap: 0 }
            }
          >
            <AvatarWithStatusMenu />
            <Box sx={{ display: 'flex', alignItems: 'center', gridGap: '8px' }}>
              <NotificationsAnnounceKitAction />
              {consoleHeader ? (
                <AdminAction user={user} />
              ) : (
                <AgentChatAction />
              )}
              <LogoutAction />
              <ConversationalAgent />
            </Box>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default HeaderMenu;
